import {
  ResourceLibraryCategoryViewResponse,
  ResourceLibraryItemCreationResponse,
  ResourceLibraryItemViewResponse,
} from "@design-controls/types";
import { PRODUCT_RESOURCE_SUBTYPE } from "../constants/resourceLibrary";
import { medtechApi } from "./medtech";

export const ResourceLibraryAPI = {
  /**
   * Creates a new product in the resource library.
   *
   * Needs to make a precursor call to get the PK ID of the
   * category which, unlike the matrix ID, is not fixed.
   *
   * @see {@link ResourceLibraryAPI.getProductById} for
   * retrieving the product after creation.
   */
  async createProduct(
    instanceId: number,
    displayId: string,
    displayName: string,
  ): Promise<ResourceLibraryItemCreationResponse> {
    const { id: categoryPkId } = await getProductCategoryInfo(instanceId);

    const res = await medtechApi.post(`/v2/${instanceId}/data-registry/items`, {
      display_id: displayId,
      name: displayName,
      category_id: PRODUCT_RESOURCE_SUBTYPE,
      registry_category_pk_id: categoryPkId,
    });

    return ResourceLibraryItemCreationResponse.parse(res.data);
  },

  async getProductById(
    instanceId: number,
    productId: number,
  ): Promise<ResourceLibraryItemViewResponse> {
    const res = await medtechApi.get(
      `/v2/${instanceId}/data-registry/items/${productId}`,
    );
    return ResourceLibraryItemViewResponse.parse(res.data);
  },
} as const;

/**
 * Not a part of our public API contract,
 * this is used internally when we need to get
 * the PK ID of the product category.
 *
 * If you need to call this from a hook, rather
 * than the ResourceLibraryAPI object above,
 * then something has gone wrong with your
 * approach.
 */
async function getProductCategoryInfo(
  instanceId: number,
): Promise<ResourceLibraryCategoryViewResponse> {
  const res = await medtechApi.get(
    `/v2/${instanceId}/data-registry/categories/${PRODUCT_RESOURCE_SUBTYPE}/view`,
  );
  return ResourceLibraryCategoryViewResponse.parse(res.data);
}
